/**
 * Component with instructions for using API keys in the Chrome extension
 */

import { useState } from 'react';

export function ExtensionInstructions() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6'>
      <div className='flex items-start justify-between'>
        <div className='flex items-start space-x-3'>
          <div className='flex-shrink-0'>
            <svg
              className='w-6 h-6 text-blue-600 dark:text-blue-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <div className='flex-1'>
            <h3 className='text-lg font-semibold text-blue-800 dark:text-blue-200'>
              Cómo usar las API Keys en la extensión
            </h3>
            <p className='text-blue-700 dark:text-blue-300 text-sm mt-1'>
              Sigue estos pasos para conectar la extensión de Chrome con tu
              cuenta.
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className='flex-shrink-0 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200'
        >
          <svg
            className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M19 9l-7 7-7-7'
            />
          </svg>
        </button>
      </div>

      {isExpanded && (
        <div className='mt-4 space-y-4'>
          <div className='bg-white dark:bg-gray-800 rounded-md p-4 border border-blue-200 dark:border-blue-700'>
            <h4 className='font-semibold text-gray-900 dark:text-white mb-3'>
              📋 Pasos para configurar la extensión:
            </h4>
            <ol className='space-y-3 text-sm text-gray-700 dark:text-gray-300'>
              <li className='flex items-start space-x-3'>
                <span className='flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full flex items-center justify-center text-xs font-semibold'>
                  1
                </span>
                <div>
                  <strong>Crea una API Key:</strong> Haz clic en "Crear Nueva
                  API Key" arriba y dale un nombre descriptivo.
                </div>
              </li>
              <li className='flex items-start space-x-3'>
                <span className='flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full flex items-center justify-center text-xs font-semibold'>
                  2
                </span>
                <div>
                  <strong>Copia la API Key:</strong> Guarda la clave completa
                  que empieza con{' '}
                  <code className='bg-gray-100 dark:bg-gray-700 px-1 rounded'>
                    grd_
                  </code>{' '}
                  (solo se muestra una vez).
                </div>
              </li>
              <li className='flex items-start space-x-3'>
                <span className='flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full flex items-center justify-center text-xs font-semibold'>
                  3
                </span>
                <div>
                  <strong>Instala la extensión:</strong> Descarga e instala la
                  extensión de Chrome desde la Chrome Web Store.
                </div>
              </li>
              <li className='flex items-start space-x-3'>
                <span className='flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full flex items-center justify-center text-xs font-semibold'>
                  4
                </span>
                <div>
                  <strong>Configura la extensión:</strong> Abre la extensión y
                  pega tu API Key en el campo correspondiente.
                </div>
              </li>
              <li className='flex items-start space-x-3'>
                <span className='flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full flex items-center justify-center text-xs font-semibold'>
                  5
                </span>
                <div>
                  <strong>¡Listo!</strong> Ya puedes usar la extensión para
                  evaluar contenido web.
                </div>
              </li>
            </ol>
          </div>

          <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3'>
            <div className='flex items-start space-x-2'>
              <svg
                className='w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
              <div>
                <h5 className='font-semibold text-amber-800 dark:text-amber-200 text-sm'>
                  Importante:
                </h5>
                <p className='text-amber-700 dark:text-amber-300 text-sm mt-1'>
                  Guarda tu API Key en un lugar seguro. No la compartas con
                  nadie y si crees que ha sido comprometida, elimínala y crea
                  una nueva.
                </p>
              </div>
            </div>
          </div>

          <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3'>
            <div className='flex items-start space-x-2'>
              <svg
                className='w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <div>
                <h5 className='font-semibold text-green-800 dark:text-green-200 text-sm'>
                  Tip:
                </h5>
                <p className='text-green-700 dark:text-green-300 text-sm mt-1'>
                  Puedes crear múltiples API Keys para diferentes dispositivos o
                  propósitos. Cada una puede ser activada/desactivada
                  independientemente.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
